import { createAsyncThunk } from '@reduxjs/toolkit'
import { SOMETHING_WHEN_WRONG } from 'constant/errorMessages'
import {
  getSupplyOrderDetailAPI,
  getSupplyOrderDetailItemAPI,
  GetSupplyOrderDetailItemResponseType,
  getSupplyOrderTypeAPI,
  getSupplyOrderRemarksAPI,
  putSupplyOrderDetailAPI,
  PutSupplyOrderDetailRequestType,
} from 'features/SupplyOrder/services/outboundSO'
import { getAllLocations } from 'features/Location/services/location'
import { callErrorMsg } from 'helpers/errorMsg'
import { GetPurchaseOrderReferencesResponseType } from 'features/PurchaseOrder/services/references'
import { getPurchaseOrderApi } from 'features/PurchaseOrder/services/purchaseOrder'

export const SLICE_NAME = 'OUTBOUND_SO_DETAIL'

export const fetchGetSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderDetail`,
  async (supplyOrderId: number, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderDetailAPI(supplyOrderId)

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderDetailItem = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderDetailItem`,
  async (
    props: {
      supplyOrderId: number
      params: Partial<{
        pageIndex: number
        pageSize: number
        name: string
      }>
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const {
        outboundSODetail: {
          products: { query },
        },
      } = getState() as StoreStateType

      const { supplyOrderId, params } = props
      const response = await getSupplyOrderDetailItemAPI(supplyOrderId, { ...query, ...params })

      const result: GetSupplyOrderDetailItemResponseType['data'] = {
        content: response.data.data.content,
        pageable: response.data.data.pageable,
        numberOfElements: response.data.data.numberOfElements,
      }

      return result.content
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderType = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderType`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderTypeAPI()

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetAllLocation = createAsyncThunk(
  `${SLICE_NAME}/fetchGetAllLocation`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getAllLocations()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetSupplyOrderRemarks = createAsyncThunk(
  `${SLICE_NAME}/fetchGetSupplyOrderRemarks`,
  async (_, { rejectWithValue }) => {
    try {
      const response = await getSupplyOrderRemarksAPI()

      return response.data.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchGetPurchaseOrderReferences = createAsyncThunk(
  `${SLICE_NAME}/fetchGetPurchaseOrderReferences`,
  async (params: { poNumber?: string }, { rejectWithValue }) => {
    try {
      const { data } = await getPurchaseOrderApi({
        page_size: 30,
        status: ['PARTIALLY_FULFILLED', 'COMPLETED'],
        include_all: true,
        ...(params.poNumber ? { po_number: params.poNumber } : {}),
      })

      const backwardCompatibilityResponse: GetPurchaseOrderReferencesResponseType['data']['content'] =
        data.data.map((po) => ({
          poId: po.id,
          poNumber: po.purchase_order_number,
          destinationId: po.destination.id,
          destinationName: po.destination.name,
          vendorId: po.vendor.id,
          vendorName: po.vendor.name,
        }))

      return backwardCompatibilityResponse
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)

export const fetchPutSupplyOrderDetail = createAsyncThunk(
  `${SLICE_NAME}/fetchPutSupplyOrderDetail`,
  async (
    props: {
      supplyOrderId: number
      payload: PutSupplyOrderDetailRequestType
      onSuccess?: () => void
    },
    { rejectWithValue },
  ) => {
    try {
      const { supplyOrderId, payload, onSuccess } = props
      const response = await putSupplyOrderDetailAPI(supplyOrderId, payload)

      onSuccess?.()

      return response.data
    } catch (err) {
      callErrorMsg(err)
      return rejectWithValue(SOMETHING_WHEN_WRONG)
    }
  },
)
